import { useNavigate } from 'react-router-dom';
import { FormEvent, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'material-react-toastify';
import { useThemeColor } from '../../hooks/useThemeColor';
import {
  validateEmail,
  validatePassword,
  validatePhoneNumber,
} from '../../utils/Validation';
import { login, register } from '../../api/Auth';
import { storeToken } from '../../utils/Auth';
import { useAnalytics } from '../../analytics/useAnalytics';
import { useLocalizedText } from '../../hooks/useLocalizedText';
import { sharedColors } from '../../utils/Style';
import { useSelfAffiliateCode } from '../../hooks/useSelfAffiliateCode';

export const RegisterForm = () => {
  const t = useLocalizedText();

  const navigate = useNavigate();
  const { primary, secondary } = useThemeColor();
  const { identify, completeRegistration } = useAnalytics();
  const { refresh: refreshSelfAffiliateCode } = useSelfAffiliateCode();

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const isEmailValid = validateEmail(email);
  const isPasswordValid = validatePassword(password);
  const isPhoneValid = validatePhoneNumber(phone);

  const handleRegister = (ev: FormEvent) => {
    ev.preventDefault();

    setLoading(true);
    register({ email, phone, password })
      .then(() => {
        toast.success(t('register.success'));
        completeRegistration({ email, phone });
        login(email, password)
          .then((token) => {
            storeToken(token);
            identify({ email });
            refreshSelfAffiliateCode();
            navigate('/');
          })
          .finally(() => setLoading(false));
      })
      .catch((err) => {
        toast.error(err.message);
        setLoading(false);
      });
  };

  return (
    <Paper
      component='form'
      onSubmit={handleRegister}
      elevation={3}
      sx={{
        px: 2.5,
        py: 2,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        width: '100%',
        height: '100%',
      }}
    >
      <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
        {t('register.register')}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: '100%',
        }}
      >
        <Typography>{t('register.email')}</Typography>

        <TextField
          size='small'
          fullWidth
          disabled={loading}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={!!email.length && !isEmailValid}
          helperText={
            isEmailValid || !email.length
              ? undefined
              : t('validation.valid_email')
          }
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: '100%',
        }}
      >
        <Typography>{t('register.phone_number')}</Typography>

        <TextField
          size='small'
          fullWidth
          disabled={loading}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={!!phone.length && !isPhoneValid}
          helperText={
            isPhoneValid || !phone.length
              ? undefined
              : t('validation.phone_number_length')
          }
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: '100%',
        }}
      >
        <Typography>{t('register.password')}</Typography>
        <TextField
          size='small'
          fullWidth
          disabled={loading}
          type='password'
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={!!password.length && !isPasswordValid}
          helperText={
            isPasswordValid || !password.length
              ? undefined
              : t('validation.password_length')
          }
        />
      </Box>

      <Typography sx={{ fontSize: '12px', mb: -1, color: sharedColors.gray5 }}>
        {t('register.agreements_info')}
        <Link
          href='/sayfa/kisisel-veriler-politikasi'
          target='_blank'
          sx={{ color: 'inherit' }}
        >
          {t('register.kvkk_agreement')}
        </Link>
        ,{' '}
        <Link
          href='/sayfa/gizlilik-ve-guvenlik'
          target='_blank'
          sx={{ color: 'inherit' }}
        >
          {t('register.privacy_policy')}
        </Link>
      </Typography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
          alignItems: 'center',
        }}
      >
        <Button
          variant='contained'
          type='submit'
          disabled={
            loading || !isEmailValid || !isPasswordValid || !isPhoneValid
          }
          sx={{
            textTransform: 'none',
            backgroundColor: primary,
            color: secondary,
          }}
        >
          {t('register.register')}
        </Button>

        {loading && <CircularProgress color='primary' />}
      </Box>
    </Paper>
  );
};
